import { DialogSize, Icon, useDialog } from '@ics-portal/guardian';
import { type ReactNode, useCallback, useMemo } from 'react';
import type { TableAction } from '@ics-portal/ics-data-table';
import { ComponentCreateRequestStatusCode } from '@gql/graphql';
import { humanizeRequestStatus } from '@pages/notification-components/utils/humanizeRequestStatus';
import { ActionDialog } from './components/ActionDialog';
import { EditRequestDialog } from './components/EditRequestDialog';
import { getActionDialogName } from './utils/getActionDialogName';
import { type RequestRow } from './columns';

export interface RequestActionDefinition {
    code: ComponentCreateRequestStatusCode;
    icon: ReactNode;
    size?: DialogSize;
}

export const REQUEST_ACTIONS_DATA: RequestActionDefinition[] = [
    {
        code: ComponentCreateRequestStatusCode.Approved,
        icon: <Icon name="check_circle" />,
    },
    {
        code: ComponentCreateRequestStatusCode.Forwarded,
        icon: <Icon name="send_money" />,
    },
    {
        code: ComponentCreateRequestStatusCode.Replaced,
        icon: <Icon name="published_with_changes" />,
        size: DialogSize.md,
    },
    {
        code: ComponentCreateRequestStatusCode.Removed,
        icon: <Icon name="block" />,
    },
];

export function isRequestActionSupportedByAll(
    code: ComponentCreateRequestStatusCode,
    rows: RequestRow[],
): boolean {
    return (
        rows.length > 0 &&
        rows.every((row) =>
            row.statusTransitions.some(
                (transition) => transition.code === code,
            ),
        )
    );
}

export function isRequestActionApplicable(
    code: ComponentCreateRequestStatusCode,
    rows: RequestRow[],
): boolean {
    return (
        rows.length > 0 &&
        rows.every((row) =>
            row.statusTransitions.some(
                (transition) => transition.code === code && !transition.blocked,
            ),
        )
    );
}

export function getRequestActionBlockedTooltip(
    code: ComponentCreateRequestStatusCode,
    rows: RequestRow[],
): string | undefined {
    const lines = rows.reduce<string[]>((acc, row) => {
        const transition = row.statusTransitions.find((t) => t.code === code);
        if (!transition) {
            acc.push(
                `Blocked by ${row.componentName}: Status is ${humanizeRequestStatus(row.statusCode)}`,
            );
        } else if (transition.blocked) {
            acc.push(
                `Blocked by ${row.componentName}: ${transition.reason ?? 'Unknown'}`,
            );
        }
        return acc;
    }, []);

    return lines.length > 0 ? lines.join('\n') : undefined;
}

export function useShowRequestActionModal(): (
    code: ComponentCreateRequestStatusCode,
    selectedRows: RequestRow[],
    size?: DialogSize,
) => void {
    const { form } = useDialog();

    return useCallback(
        (
            code: ComponentCreateRequestStatusCode,
            selectedRows: RequestRow[],
            size?: DialogSize,
        ): void => {
            const actionName = getActionDialogName(code);
            form({
                title: `${actionName} request`,
                content: (
                    <ActionDialog action={code} selectedRows={selectedRows} />
                ),
                size: size ?? DialogSize.sm,
                buttons: {
                    submitLabel: actionName,
                },
            });
        },
        [form],
    );
}

export function useShowEditRequestModal(): (request: RequestRow) => void {
    const { form } = useDialog();

    return useCallback(
        (request: RequestRow): void => {
            form({
                title: 'Edit request',
                content: <EditRequestDialog request={request} />,
                size: DialogSize.md,
                buttons: {
                    submitLabel: 'Save',
                },
            });
        },
        [form],
    );
}

export function useRequestActions(): TableAction<RequestRow>[] {
    const showActionModal = useShowRequestActionModal();
    const showEditModal = useShowEditRequestModal();

    return useMemo<TableAction<RequestRow>[]>(() => {
        const editAction: TableAction<RequestRow> = {
            id: 'edit',
            icon: <Icon name="edit" />,
            variant: 'secondary',
            label: (ctx) =>
                ctx.selectedRows.length === 1 &&
                ctx.selectedRows[0].statusCode ===
                    ComponentCreateRequestStatusCode.ApprovalPending &&
                'Edit',
            onClick: (ctx) => {
                const [row] = ctx.selectedRows;
                if (row) {
                    showEditModal(row);
                }
            },
        };

        const statusActions: TableAction<RequestRow>[] =
            REQUEST_ACTIONS_DATA.map((actionData) => ({
                id: actionData.code,
                icon: actionData.icon,
                variant: 'secondary',
                label: (ctx) =>
                    isRequestActionApplicable(
                        actionData.code,
                        ctx.selectedRows,
                    ) && getActionDialogName(actionData.code),
                onClick: (ctx) => {
                    showActionModal(
                        actionData.code,
                        ctx.selectedRows,
                        actionData.size,
                    );
                },
            }));

        return [editAction, ...statusActions];
    }, [showActionModal, showEditModal]);
}

export interface RequestMenuAction {
    id: ComponentCreateRequestStatusCode;
    icon: ReactNode;
    label: string;
    size?: DialogSize;
    isVisible: (rows: RequestRow[]) => boolean;
    disabled: (
        rows: RequestRow[],
    ) => { disabled: true; tooltip: string } | false;
    onClick: (rows: RequestRow[]) => void;
}

export function useRequestMenuActions(): RequestMenuAction[] {
    const showActionModal = useShowRequestActionModal();

    return useMemo(
        () =>
            REQUEST_ACTIONS_DATA.map((actionData) => ({
                id: actionData.code,
                icon: actionData.icon,
                label: getActionDialogName(actionData.code),
                size: actionData.size,
                isVisible: (rows: RequestRow[]) => rows.length > 0,
                disabled: (rows: RequestRow[]) => {
                    const tooltip = getRequestActionBlockedTooltip(
                        actionData.code,
                        rows,
                    );
                    return tooltip ? { disabled: true, tooltip } : false;
                },
                onClick: (rows: RequestRow[]) => {
                    showActionModal(actionData.code, rows, actionData.size);
                },
            })),
        [showActionModal],
    );
}
